import { useSearchParams } from 'react-router-dom';
import { useCallback, useMemo, useState } from 'react';
import {
  defaultSidebarFilterParamKeys,
  parseFiltersFromParams,
  writeFiltersToParams,
  clearFiltersInParams,
  hasDateSelection,
} from './utils';
import type { SidebarSearchConfig, SidebarSearchState, SidebarAppliedFilters } from './types';

export const useSidebarSearchAndFilters = (config: SidebarSearchConfig): SidebarSearchState => {
  const { filterParamKey, defaultFilterValue, filterParamKeys = defaultSidebarFilterParamKeys } = config;
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState('');

  const updateParams = useCallback(
    (updater: (params: URLSearchParams) => void) =>
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        updater(next);
        return next;
      }),
    [setSearchParams]
  );

  const clearSearch = useCallback(() => {
    setSearchValue('');
  }, [setSearchValue]);

  const filterValue = filterParamKey != null ? searchParams.get(filterParamKey) ?? defaultFilterValue : undefined;

  const setFilterValue = useCallback(
    (next: string) => {
      if (!filterParamKey) return;
      updateParams((p) => {
        const isDefault = defaultFilterValue != null && next === defaultFilterValue;
        isDefault || !next ? p.delete(filterParamKey) : p.set(filterParamKey, next);
      });
    },
    [filterParamKey, defaultFilterValue, updateParams]
  );

  const appliedFilters = useMemo(() => {
    return parseFiltersFromParams(searchParams, filterParamKeys);
  }, [searchParams, filterParamKeys]);

  const setAppliedFilters = useCallback(
    (next: SidebarAppliedFilters) =>
      updateParams((p) => {
        writeFiltersToParams(p, next, filterParamKeys);
      }),
    [filterParamKeys, updateParams]
  );

  const resetAppliedFilters = useCallback(
    () => updateParams((p) => clearFiltersInParams(p, filterParamKeys)),
    [filterParamKeys, updateParams]
  );

  const resetFilters = useCallback(() => {
    setSearchValue('');
    updateParams((p) => {
      if (filterParamKey) {
        p.delete(filterParamKey);
      }
      clearFiltersInParams(p, filterParamKeys);
    });
  }, [filterParamKey, filterParamKeys, updateParams]);

  return {
    searchValue,
    filterValue,
    clearSearch,
    resetFilters,
    appliedFilters,
    setFilterValue,
    setSearchValue,
    setAppliedFilters,
    resetAppliedFilters,
    hasDateFilterSelection: hasDateSelection(appliedFilters.date),
  };
};
